import apiClient from './apiClient'
import axios, { CancelTokenSource } from 'axios'
import { forcedStreamError } from '../utils/devErrors'

// Which company's assistant a chat talks to (mirrors the backend Company enum).
export type Company = 'gails' | 'tbf'

export const COMPANY_LABELS: Record<Company, string> = {
  gails: "Gail's",
  tbf: 'The Bread Factory',
}

// Define MessageData interface locally to avoid circular dependencies
export interface MessageData {
  id: string
  role: 'user' | 'assistant' | 'error'
  content: string
  timestamp: number
  documents?: DocumentReference[]
  daxQueries?: DaxQuery[]
  files?: GeneratedFile[]
  streamingComplete?: boolean
  feedback?: FeedbackType | null
  // Only set on role === 'error': which failure class this was, and whether
  // offering the user a retry makes sense.
  errorCode?: string
  retryable?: boolean
}

interface QueryInput {
  messages: MessageData[]
  user_id: string
  chat_id: string
}

interface QueryOutput {
  message: string
}

interface DocumentReference {
  filename: string
  file_id: string
  relevant_pages?: [number, number]
  web_url?: string
}

export interface DaxQuery {
  title: string
  dax: string
  model_name?: string | null
  // Results are captured/stored but not displayed in the UI.
  rows?: Record<string, unknown>[] | null
  row_count?: number | null
  error?: string | null
}

export enum FeedbackType {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
}

export interface MessageFeedbackData {
  type: FeedbackType
  reason?: string | null
}

// A file the agent generated. Stored in blob storage and kept indefinitely;
// what travels is only its path. Downloads go through an app endpoint that
// checks the chat is yours and mints a fresh short-lived link per click, so the
// card keeps working forever — nothing signed is ever held here.
//
// `blob_path` is absent on files from before exports were stored, which is how
// the UI knows to show them as no longer available.
export interface GeneratedFile {
  filename: string
  mime: string
  blob_path?: string | null
  row_count?: number | null
  byte_size?: number | null
}

/** Where to click to download `file`; the endpoint redirects to storage. */
export const fileDownloadUrl = (
  chatId: string,
  messageId: string,
  filename: string
): string => {
  const base = apiClient.defaults.baseURL || ''
  return `${base}/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(
    messageId
  )}/files/${encodeURIComponent(filename)}`
}

// One step in the progress trail the agent streams while it works. Steps are
// keyed by `id`: receiving the same id again updates that step in place. An
// empty `label` means "keep whatever is already shown, only change the state".
export interface StatusUpdate {
  id: string
  label: string
  state: 'active' | 'done' | 'warning'
  kind: 'agent' | 'tool' | 'search' | 'thinking' | 'retry'
  detail?: string | null
}

// Mirrors StreamError in the agent's src/models.py. `code` lets the UI decide
// what to say and whether a retry is worth offering; `detail` is for the console.
export interface StreamErrorPayload {
  code: string
  error: string
  retryable: boolean
  detail?: string | null
}

interface StreamChunk {
  type:
    | 'response_text'
    | 'relevant_documents'
    | 'dax_queries'
    | 'error'
    | 'message_id'
    | 'status'
    | 'chat_title'
    | 'done'
    | 'file'
  content:
    | string
    | DocumentReference[]
    | DaxQuery[]
    | GeneratedFile[]
    | StatusUpdate
    | StreamErrorPayload
    | object
    | null
}

// Shown when the stream dies without the agent's `done` marker — a proxy
// timeout or a dropped connection, which previously ended as a spinner that
// simply vanished with nothing written to the chat.
const TRUNCATED_STREAM_ERROR: StreamErrorPayload = {
  code: 'connection',
  error:
    'The connection was interrupted before BreadGPT finished answering. Please try again.',
  retryable: true,
  detail: 'stream ended without a done marker',
}


// Accepts whatever the server put in an `error` chunk (new structured payload or
// the old `{error: string}`) and always returns the structured shape.
function normaliseStreamError(content: unknown): StreamErrorPayload {
  if (content && typeof content === 'object') {
    const c = content as Partial<StreamErrorPayload>
    if (typeof c.error === 'string' && c.error.trim()) {
      return {
        code: typeof c.code === 'string' ? c.code : 'unknown',
        error: c.error,
        retryable: c.retryable === true,
        detail: typeof c.detail === 'string' ? c.detail : null,
      }
    }
  }
  return {
    code: 'unknown',
    error: 'Something went wrong while generating a response. Please try again.',
    retryable: true,
    detail: typeof content === 'string' ? content : null,
  }
}

// A non-200 arrives before any streaming starts, so the body is a normal JSON
// error from FastAPI — use its `detail` rather than a bare status code.
async function errorFromResponse(
  response: Response
): Promise<StreamErrorPayload> {
  let detail = ''
  try {
    const body = await response.json()
    detail = typeof body?.detail === 'string' ? body.detail : ''
  } catch {
    // Non-JSON body; the status alone will have to do.
  }
  if (response.status === 401) {
    return {
      code: 'auth',
      error: 'Your session has expired. Please sign in again.',
      retryable: false,
      detail,
    }
  }
  if (response.status === 403) {
    return {
      code: 'permission',
      error: detail || 'You do not have access to this assistant.',
      retryable: false,
      detail,
    }
  }
  if (response.status === 429) {
    return {
      code: 'rate_limit',
      error: 'BreadGPT is busy right now. Please try again in a moment.',
      retryable: true,
      detail,
    }
  }
  if (response.status >= 500) {
    return {
      code: 'upstream_unavailable',
      error: 'BreadGPT is temporarily unavailable. Please try again.',
      retryable: true,
      detail: detail || `HTTP ${response.status}`,
    }
  }
  return {
    code: 'bad_request',
    error: detail || 'BreadGPT could not process that request.',
    retryable: false,
    detail: detail || `HTTP ${response.status}`,
  }
}

export const getChatResponse = async (
  messages: MessageData[],
  userId: string,
  chatId: string,
  onStreamUpdate: (chunk: string) => void,
  onDocumentsReceived: (documents: DocumentReference[]) => void,
  onNewResponseId: (responseId: string) => void,
  cancelToken: CancelTokenSource,
  onDaxQueriesReceived?: (daxQueries: DaxQuery[]) => void,
  company?: Company,
  onError?: (error: StreamErrorPayload) => void,
  onStatus?: (status: StatusUpdate) => void,
  onFilesReceived?: (files: GeneratedFile[]) => void
): Promise<string> => {
  // If we have a streaming handler, use fetch for streaming
  if (onStreamUpdate) {
    try {
      // Dev-only: surface a forced failure through the same error path as a real one.
      const forced = forcedStreamError(`/chats/${chatId}/messages`)
      if (forced) {
        onError?.(forced)
        return ''
      }
      // Get the base URL from apiClient
      const baseUrl = apiClient.defaults.baseURL || ''

      // Create AbortController to handle cancellation
      const controller = new AbortController()

      // Link the axios cancelToken to the AbortController
      if (cancelToken) {
        cancelToken.cancel = () => {
          controller.abort()
          return 'Request canceled'
        }
      }

      const response = await fetch(`${baseUrl}/chats/${chatId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key':
            (apiClient.defaults.headers['X-API-Key'] as string) || '',
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        body: JSON.stringify({
          messages,
          user_id: userId,
          company: company ?? null,
        }),
        signal: controller.signal,
      })

      if (!response.ok) {
        onError?.(await errorFromResponse(response))
        return ''
      }

      // Process the stream
      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('Unable to read response stream')
      }

      const decoder = new TextDecoder()
      let fullResponse = ''
      let buffer = ''
      // The agent ends every turn with a `done` chunk. Its absence is the only
      // reliable signal that the response was cut short rather than completed.
      let sawDone = false
      let sawError = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Process complete lines from the buffer
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep the incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonData = line.substring(6) // Remove 'data: ' prefix
              if (jsonData.trim() === '') continue // Skip empty data lines

              const chunk: StreamChunk = JSON.parse(jsonData)
              if (
                chunk.type === 'response_text' &&
                typeof chunk.content === 'string'
              ) {
                fullResponse += chunk.content
                onStreamUpdate(chunk.content)
              } else if (
                chunk.type === 'relevant_documents' &&
                Array.isArray(chunk.content)
              ) {
                onDocumentsReceived(chunk.content as DocumentReference[])
              } else if (
                chunk.type === 'dax_queries' &&
                Array.isArray(chunk.content)
              ) {
                onDaxQueriesReceived?.(chunk.content as DaxQuery[])
              } else if (
                chunk.type === 'file' &&
                Array.isArray(chunk.content)
              ) {
                onFilesReceived?.(chunk.content as GeneratedFile[])
              } else if (chunk.type === 'status') {
                onStatus?.(chunk.content as StatusUpdate)
              } else if (chunk.type === 'done') {
                sawDone = true
              } else if (chunk.type === 'error') {
                console.error('Stream error:', chunk.content)
                sawError = true
                onError?.(normaliseStreamError(chunk.content))
              } else if (chunk.type === 'message_id') {
                onNewResponseId(chunk.content as string)
              }
            } catch (parseError) {
              // If JSON parsing fails, treat as old format (fallback)
              console.warn(
                'Failed to parse JSON chunk, treating as plain text:',
                parseError
              )
              const plainText = line.startsWith('data: ')
                ? line.substring(6)
                : line
              if (plainText.trim()) {
                fullResponse += plainText
                onStreamUpdate(plainText)
              }
            }
          }
        }
      }

      if (!sawDone && !sawError) {
        onError?.(TRUNCATED_STREAM_ERROR)
      }

      return fullResponse
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        // The user cancelled, or the chat was switched. Not a failure.
        return 'Request canceled'
      }
      console.error('Error getting chat response:', error)
      // A mid-stream network fault lands here. Report it through the same path
      // as a server-sent error so the caller has one way to handle failure.
      onError?.({
        code: 'connection',
        error:
          'Lost the connection to BreadGPT. Check your network and try again.',
        retryable: true,
        detail: error instanceof Error ? error.message : String(error),
      })
      return ''
    }
  }
  // For non-streaming requests, use the original axios implementation
  else {
    try {
      const response = await apiClient.post<QueryOutput>(
        '/query',
        {
          messages,
          user_id: userId,
          chat_id: chatId,
        } as QueryInput,
        {
          cancelToken: cancelToken?.token,
        }
      )

      return response.data.message
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request canceled:', error.message)
        return 'Request canceled'
      }
      console.error('Error getting chat response:', error)
      throw error
    }
  }
}

// Create a cancel token source
export const createCancelTokenSource = (): CancelTokenSource => {
  return axios.CancelToken.source()
}

export type { DocumentReference }

export const addMessageFeedback = async (
  chatId: string,
  messageId: string,
  feedbackData: MessageFeedbackData | null,
  cancelToken?: CancelTokenSource
): Promise<void> => {
  try {
    await apiClient.post(
      `/chats/${chatId}/messages/${messageId}/feedback`,
      feedbackData,
      {
        cancelToken: cancelToken?.token,
      }
    )
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Add feedback request canceled:', error.message)
    } else {
      console.error('Error adding message feedback:', error)
    }
    // Re-throw the error if you want the caller to handle it,
    // or handle it here as appropriate for your app's error handling strategy.
    throw error
  }
}

// Chat management interfaces
export interface ChatOverview {
  id: string
  title: string | null
  timestamp: number
}

// Backend message format (what the API returns)
interface BackendMessageData {
  id: string
  role: 'user' | 'assistant' | 'error'
  error_code?: string | null
  retryable?: boolean | null
  content: string
  timestamp: number
  relevant_documents?: DocumentReference[]
  dax_queries?: DaxQuery[]
  files?: GeneratedFile[]
  feedback?: { type: FeedbackType; reason?: string | null } | null
}

// Backend chat format
interface BackendChat {
  id: string
  title: string | null
  user_id: string
  source: string
  messages: BackendMessageData[]
  company?: Company | null
  deleted_at: number | null
}

export interface Chat {
  id: string
  title: string | null
  user_id: string
  source: string
  messages: MessageData[]
  company?: Company | null
  deleted_at: number | null
}

// Get all chats overview for the current user
export const getChatsOverview = async (
  cancelToken?: CancelTokenSource
): Promise<ChatOverview[]> => {
  try {
    const response = await apiClient.get<ChatOverview[]>('/chats/overview', {
      cancelToken: cancelToken?.token,
    })
    return response.data
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Get chats overview request canceled:', error.message)
      return []
    } else {
      console.error('Error fetching chats overview:', error)
      throw error
    }
  }
}

// Get specific chat by ID
export const getChat = async (
  chatId: string,
  cancelToken?: CancelTokenSource
): Promise<Chat> => {
  try {
    const response = await apiClient.get<BackendChat>(`/chats/${chatId}`, {
      cancelToken: cancelToken?.token,
    })

    // Transform relevant_documents to documents for frontend compatibility
    const chatData = response.data
    const transformedMessages: MessageData[] = chatData.messages.map(
      message => {
        // Transform relevant_documents to documents and preserve all other fields
        const {
          relevant_documents,
          dax_queries,
          feedback,
          error_code,
          retryable,
          ...otherFields
        } = message
        return {
          ...otherFields,
          documents: relevant_documents || undefined,
          daxQueries: dax_queries || undefined,
          files: message.files || undefined,
          feedback: feedback?.type || null,
          // A failed turn is stored like any other message, so a reloaded chat
          // still shows what was asked and what went wrong — and still offers
          // Try again where trying again could help.
          errorCode: error_code || undefined,
          retryable: retryable ?? undefined,
        }
      }
    )

    return {
      ...chatData,
      messages: transformedMessages,
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Get chat request canceled:', error.message)
      throw error
    } else {
      console.error('Error fetching chat:', error)
      throw error
    }
  }
}

// Delete chat (soft delete)
export const deleteChat = async (
  chatId: string,
  cancelToken?: CancelTokenSource
): Promise<void> => {
  try {
    await apiClient.delete(`/chats/${chatId}`, {
      cancelToken: cancelToken?.token,
    })
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Delete chat request canceled:', error.message)
    } else {
      console.error('Error deleting chat:', error)
      throw error
    }
  }
}

// Rename chat (this would require a new backend endpoint)
export const renameChat = async (
  chatId: string,
  newTitle: string,
  cancelToken?: CancelTokenSource
): Promise<void> => {
  try {
    await apiClient.patch(
      `/chats/${chatId}`,
      { title: newTitle },
      {
        cancelToken: cancelToken?.token,
      }
    )
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Rename chat request canceled:', error.message)
    } else {
      console.error('Error renaming chat:', error)
      throw error
    }
  }
}
